import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const TYPE_OPTIONS = [
  { value: 'call', label: 'Phone Call' },
  { value: 'email', label: 'Email' },
  { value: 'meeting', label: 'Meeting' },
  { value: 'site_visit', label: 'Site Visit' },
  { value: 'proposal', label: 'Send Proposal' },
  { value: 'demo', label: 'Demo' },
  { value: 'follow_up', label: 'General Follow-up' },
  { value: 'other', label: 'Other' },
];

const STATUS_OPTIONS = [
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'overdue', label: 'Overdue' },
];

const toLocalDatetimeValue = (isoString) => {
  if (!isoString) return '';
  const d = new Date(isoString);
  const pad = n => String(n)?.padStart(2, '0');
  return `${d?.getFullYear()}-${pad(d?.getMonth()+1)}-${pad(d?.getDate())}T${pad(d?.getHours())}:${pad(d?.getMinutes())}`;
};

const FollowupModal = ({ isOpen, onClose, onSave, followup, prospectId }) => {
  const [formData, setFormData] = useState({
    title: '', description: '', interactionType: 'call',
    scheduledAt: '', followupStatus: 'scheduled'
  });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (followup) {
      setFormData({
        title: followup?.title || '',
        description: followup?.description || '',
        interactionType: followup?.interactionType || 'call',
        scheduledAt: toLocalDatetimeValue(followup?.scheduledAt),
        followupStatus: followup?.followupStatus || 'scheduled'
      });
    } else {
      const tomorrow = new Date();
      tomorrow?.setDate(tomorrow?.getDate() + 1);
      tomorrow?.setHours(9, 0, 0, 0);
      setFormData({
        title: '', description: '', interactionType: 'call',
        scheduledAt: toLocalDatetimeValue(tomorrow?.toISOString()),
        followupStatus: 'scheduled'
      });
    }
    setErrors({});
  }, [followup, isOpen]);

  const validate = () => {
    const errs = {};
    if (!formData?.title?.trim()) errs.title = 'Title is required';
    if (!formData?.scheduledAt) errs.scheduledAt = 'Date and time is required';
    setErrors(errs);
    return Object.keys(errs)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!validate()) return;
    setSaving(true);
    try {
      await onSave({ ...formData, prospectId, scheduledAt: new Date(formData.scheduledAt)?.toISOString() });
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-card rounded-lg shadow-xl w-full max-w-md mx-4">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 className="text-lg font-semibold text-foreground">
            {followup ? 'Edit Follow-up' : 'Schedule Follow-up'}
          </h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <Icon name="X" size={20} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <Input label="Title" required value={formData?.title}
            onChange={e => setFormData(p => ({ ...p, title: e?.target?.value }))}
            error={errors?.title} placeholder="e.g. Follow-up call on proposal" />
          <Select label="Type" options={TYPE_OPTIONS} value={formData?.interactionType}
            onChange={v => setFormData(p => ({ ...p, interactionType: v }))} />
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">Date & Time <span className="text-destructive">*</span></label>
            <input type="datetime-local" className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              value={formData?.scheduledAt}
              onChange={e => setFormData(p => ({ ...p, scheduledAt: e?.target?.value }))} />
            {errors?.scheduledAt && <p className="text-sm text-destructive">{errors?.scheduledAt}</p>}
          </div>
          {followup && (
            <Select label="Status" options={STATUS_OPTIONS} value={formData?.followupStatus}
              onChange={v => setFormData(p => ({ ...p, followupStatus: v }))} />
          )}
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">Notes</label>
            <textarea className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring min-h-[70px] resize-y"
              value={formData?.description}
              onChange={e => setFormData(p => ({ ...p, description: e?.target?.value }))}
              placeholder="Additional notes..." />
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" loading={saving} iconName="Calendar" iconPosition="left">
              {followup ? 'Save Changes' : 'Schedule'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default FollowupModal;
